import { Box, Typography } from '@mui/material';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { areResourcesEditable, isHoursEditable } from '../cells/editFields';
import { InlineTextCell } from '../cells/InlineTextCell';
import type { CommitIntent } from '../cells/InlineTextCell';
import { useFieldEditing } from '../cells/useFieldEditing';
import type { FlatRow } from '../editor';

import { AllocationStack } from './AllocationStack';
import { TimelineBar } from './TimelineBar';
import type { BarDragMode } from './TimelineBar';
import {
  BAR_HEIGHT_PX,
  LEFT_COLUMN_WIDTH_PX,
  TARGET_HIT_WIDTH_PX,
  TIMELINE_INDENT_PX,
  TIMELINE_ROW_HEIGHT_PX,
} from './timelineGeometry';
import { barGeometry } from './timelineModel';
import type { TimelineSpan } from './timelineModel';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MonoText } from '@/components/ui';
import { formatShortDate } from '@/lib';
import { zIndex } from '@/theme';

export type TimelineRowProps = {
  row: FlatRow;
  /** The bar to draw: own dates unioned with descendants'. null = nothing to draw. */
  span: TimelineSpan | null;
  isParent: boolean;
  /** Where the children sit inside a parent's bar; null on a leaf. */
  childrenSpan?: TimelineSpan | null;
  /** The bar is only the today → target default; nothing is stored (ADR 0050). */
  provisional?: boolean;
  /** Day offset of the node's own targetDate; null when absent or off-window. */
  targetOffset?: number | null;
  barStartIso: string;
  barEndIso: string;
  dayCount: number;
  /** Current zoom, px per day. */
  dayWidth: number;
  hoursLabel: string;
  barLabel: string;
  active: boolean;
  dragging: boolean;
  /** Part of the subtree currently being dragged to a new position. */
  inDraggedBlock?: boolean;
  /** Briefly highlighted just after a restructure drop. */
  justMoved?: boolean;
  onSelect: () => void;
  onBarPointerDown: (mode: BarDragMode) => (event: ReactPointerEvent<HTMLElement>) => void;
  onTrackClick: (event: ReactMouseEvent<HTMLElement>) => void;
  /** Starts a restructure drag; a press below the threshold selects instead. */
  onNamePointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
};

/** The clear strip above the bar — where the target glyph can always be hovered. */
const BAR_TOP_PX = (TIMELINE_ROW_HEIGHT_PX - BAR_HEIGHT_PX) / 2;

export const TimelineRow = ({
  row,
  span,
  isParent,
  childrenSpan,
  provisional = false,
  targetOffset,
  barStartIso,
  barEndIso,
  dayCount,
  dayWidth,
  hoursLabel,
  barLabel,
  active,
  dragging,
  inDraggedBlock = false,
  justMoved = false,
  onSelect,
  onBarPointerDown,
  onTrackClick,
  onNamePointerDown,
}: TimelineRowProps) => {
  const { t } = useTranslation('pmPlanning');
  const { commitField, isEditing, startEditing, stopEditing } = useFieldEditing();
  /*
    The name is editable here too — a task created in this view (⌘Enter, the
    toolbar, the kebab) arrives with its title edit already started, and
    without an input to carry it that edit state would never clear, freezing
    keyboard navigation. Existing names edit via the discreet pencil.
  */
  const editingName = isEditing(row.id, 'title');
  const editingHours = isEditing(row.id, 'hours');
  const hoursEditable = isHoursEditable(row.node);
  const unscheduled = span === null && !isParent;
  const emptyParent = span === null && isParent;
  /*
    Staffing lives on the bar for any scheduled row that can hold allocations
    (v5, decision 8) — the same predicate the list's kebab reads. It used to
    ask `kind === 'task'`, which silently left a childless top-level row with
    no stack at all: no way to add a resource, and no way to edit one.
  */
  const stack =
    span !== null && areResourcesEditable(row.node)
      ? (() => {
          const { left, width } = barGeometry(span, dayWidth);
          return { leftPx: left + width + 10 };
        })()
      : null;
  /*
    Late is the comparison the marker exists to make: the work as placed runs
    past the deadline as committed. Read off the BAR, so a parent counts as late
    when its children push it past the target — which is the case a PM most
    needs to see and the one they cannot compute by eye.
  */
  const late =
    span !== null && targetOffset !== null && targetOffset !== undefined
      ? span.endOffset > targetOffset
      : false;
  /*
    The mark's geometry and its words, in one place so the three elements that
    draw it cannot disagree.

    The readout answers the comparison the mark exists to make — the bar's end
    against the target — which is also what `late` means. Deliberately NOT
    CALC-6's days-left: that measures the target against TODAY, a different
    question whose answer would contradict the colour sitting beside it. Both
    operands are day offsets in the same window, so it is integer subtraction
    rather than date arithmetic.
  */
  /*
    Not on a dashed default bar: it ends ON the target by construction, so the
    mark would only sit on the resize handle saying "on time" about a schedule
    nobody has made yet (ADR 0050). It appears with the first drag.
  */
  const targetMark =
    targetOffset === null || targetOffset === undefined || (provisional && span !== null)
      ? null
      : {
          offset: targetOffset,
          leftPx: Math.round(targetOffset * dayWidth + dayWidth / 2),
          color: late ? 'var(--fg-warning-text)' : 'var(--color-fog-100)',
          title: (() => {
            const target = t('timeline.Target', { date: formatShortDate(row.node.targetDate) });
            if (span === null) return t('timeline.TargetUnscheduled', { target });
            const days = span.endOffset - targetOffset;
            if (days > 0) return t('timeline.TargetLate', { target, count: days });
            const spare = -days;
            return spare === 0
              ? t('timeline.TargetOnTime', { target })
              : t('timeline.TargetSpare', { target, count: spare });
          })(),
        };

  /*
    The timeline has no target-date cell — dates are dragged here — so Tab must
    stop at the end of what this view actually renders. `editFieldOrder` still
    owns the ordering; this only trims the ends, and anything else (Enter, the
    ⌘Return outline family) passes straight through.
  */
  const timelineIntent = (field: 'title' | 'hours', intent: CommitIntent): CommitIntent => {
    if (intent === 'tab-next' && (field === 'hours' || !hoursEditable)) return 'commit';
    if (intent === 'tab-prev' && field === 'title') return 'commit';
    return intent;
  };

  return (
    <Box
      data-automation-id="timeline-row"
      data-node-id={row.id}
      data-depth={row.depth}
      data-scheduled={span !== null}
      data-dragging={inDraggedBlock || undefined}
      onPointerDown={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        height: TIMELINE_ROW_HEIGHT_PX,
        borderBottom: '1px solid var(--border-divider)',
        bgcolor: active ? 'var(--bg-hover)' : 'transparent',
        opacity: inDraggedBlock ? 0.45 : 1,
        // Row affordances stay quiet until the row is engaged.
        '& .tl-add, & .tl-edit': { opacity: 0, transition: 'opacity var(--duration-instant)' },
        '&:hover .tl-add, &:focus-within .tl-add': { opacity: 1 },
        '&:hover .tl-edit, &:focus-within .tl-edit': { opacity: 1 },
        ...(justMoved ? { animation: 'dropflash var(--duration-slow) ease-out' } : {}),
      }}
    >
      {/*
        Frozen name column. `position: sticky; left: 0` inside a row that spans
        the full track width — its offset resolves against the horizontal
        scroller, so it needs no JS to stay put.
      */}
      <Box
        data-automation-id="timeline-row-name-cell"
        // The drag grip is suspended while editing, so selecting text in the
        // input cannot start a restructure drag. Both inputs live in this cell,
        // so both suspend it.
        onPointerDown={editingName || editingHours ? undefined : onNamePointerDown}
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: zIndex.rowAffordance,
          width: LEFT_COLUMN_WIDTH_PX,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          px: 2,
          pl: `${String(row.depth * TIMELINE_INDENT_PX + 16)}px`,
          bgcolor: active ? 'var(--bg-hover)' : 'var(--bg-panel)',
          borderRight: '1px solid var(--border-divider)',
          // The whole cell is the restructure grip: press selects, press and
          // move drags — the same rule the bars use.
          cursor: editingName || editingHours ? 'default' : 'grab',
          touchAction: 'none',
          ...(inDraggedBlock ? { borderLeft: '3px solid var(--color-blue-80)' } : {}),
        }}
      >
        {/*
          `isParent` (has children), not the node kind — the same value the
          bar's hatched-pink fill uses, so the glyph and the hatch can never
          disagree about what a parent is.
        */}
        {isParent && (
          <MaterialSymbol
            name="folder"
            size={16}
            color="var(--color-pink-80)"
            automationId="timeline-row-parent-icon"
          />
        )}
        {editingName ? (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <InlineTextCell
              value={row.node.name}
              display={row.node.name}
              editing
              onStartEdit={() => startEditing(row.id, 'title')}
              onCommit={(text, intent) =>
                commitField(row.node, 'title', text, timelineIntent('title', intent))
              }
              onCancel={stopEditing}
              automationId="timeline-row-name"
            />
          </Box>
        ) : (
          <Typography
            variant="bodyRegular"
            data-automation-id="timeline-row-name"
            sx={{
              flex: 1,
              minWidth: 0,
              color: 'var(--fg-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.node.name}
          </Typography>
        )}
        {!editingName && (
          <Box
            component="button"
            type="button"
            className="tl-edit"
            title={t('timeline.Rename')}
            data-automation-id="timeline-row-rename"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              startEditing(row.id, 'title');
            }}
            sx={{
              width: 18,
              height: 18,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              borderRadius: 'var(--border-radius-small)',
              bgcolor: 'transparent',
              color: 'var(--fg-3)',
              cursor: 'pointer',
              p: 0,
              '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-1)' },
            }}
          >
            <MaterialSymbol name="edit" size={13} />
          </Box>
        )}
        {/*
          Hours, editable in place — the list's cell, the list's predicate. The
          display stays the CALC-1/CALC-3 rollup (the BUDGET, not the staffing)
          while the buffer seeds from the node's own figure, as `PlanRow` does.
        */}
        {hoursEditable ? (
          <Box
            sx={{ flex: 'none' }}
            // Keeps a press on the cell out of the name column's drag grip, so
            // a click that drifts a pixel still opens the input.
            onPointerDown={(event) => event.stopPropagation()}
          >
            <InlineTextCell
              value={row.node.estimatedHours !== undefined ? String(row.node.estimatedHours) : ''}
              display={
                <MonoText size="label" color="var(--fg-3)">
                  {hoursLabel}
                </MonoText>
              }
              editing={editingHours}
              onStartEdit={() => startEditing(row.id, 'hours')}
              onCommit={(text, intent) =>
                commitField(row.node, 'hours', text, timelineIntent('hours', intent))
              }
              onCancel={stopEditing}
              align="right"
              underlineColor="var(--color-pink-80)"
              inputWidth={44}
              // Matches `labelMono`, which Figma sets a step below the sans label.
              inputSx={{ fontFamily: 'var(--font-family-mono)', fontSize: 10, fontWeight: 500 }}
              automationId="timeline-row-hours"
            />
          </Box>
        ) : (
          // Only children take the hours away from a row (BR-1/CALC-3).
          <MonoText
            size="label"
            color="var(--fg-3)"
            title={t('row.HoursRollUp')}
            data-automation-id="timeline-row-hours"
          >
            {hoursLabel}
          </MonoText>
        )}
      </Box>

      <Box
        data-automation-id="timeline-track"
        onClick={unscheduled ? onTrackClick : undefined}
        sx={{
          position: 'relative',
          width: `${String(Math.round(dayCount * dayWidth))}px`,
          flex: 'none',
          cursor: unscheduled ? 'copy' : 'default',
          transition: 'background-color .1s',
          ...(unscheduled ? { '&:hover': { bgcolor: 'var(--color-pink-10)' } } : {}),
        }}
      >
        {/*
          The deadline's HIT AREA, and the only part of the mark that is not
          inert. It is drawn BEFORE the bar deliberately: hit-testing follows
          paint order, so wherever the two overlap the bar receives the
          pointerdown and its drag still starts. That matters because a task
          scheduled by clicking the track gets `targetDate = endDate`, which
          puts the mark exactly on the bar's resize-end handle.

          Everywhere the bar is not — which for a target at or after the bar's
          end is the whole row height — this pad takes the hover and explains
          the mark. It must never stopPropagation: `trackClick` reads
          `currentTarget`, so a click bubbling from here still schedules at the
          right day.
        */}
        {targetMark && (
          <Box
            data-automation-id="timeline-target-hit"
            title={targetMark.title}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${String(targetMark.leftPx - TARGET_HIT_WIDTH_PX / 2)}px`,
              width: `${String(TARGET_HIT_WIDTH_PX)}px`,
            }}
          />
        )}

        {span !== null && (
          <TimelineBar
            span={span}
            childrenSpan={childrenSpan}
            dayWidth={dayWidth}
            startIso={barStartIso}
            endIso={barEndIso}
            status={row.node.status}
            isParent={isParent}
            provisional={provisional}
            dragging={dragging}
            label={barLabel}
            days={span.endOffset - span.startOffset + 1}
            onPointerDown={onBarPointerDown}
          />
        )}

        {/*
          The deadline, as a mark rather than text. The job is comparison — did
          this land inside the target I already committed to? — and a mark makes
          it at every zoom, where a date string needs ~100px of bar it does not
          have below about 15 days. Drawn BEFORE the allocation stack so the
          avatars sit over it when a target lands near the bar's end; the tick
          still reads through as a full-height rule.

          Colour is the whole vocabulary: neutral slate at rest, the app's
          warning orange once the work runs past the date. Not coral — that is
          the error/overdue colour, and a deadline a plan has not met yet is a
          disagreement to notice, not a failure. Not `--color-warning-80`
          either: the design system's warning is a FILL at ~1.9:1 on white, which
          a 1.5px rule cannot carry (see tokens.css).

          Inert. The hover lives on the pads, which the bar paints over; this
          only has to be seen.
        */}
        {targetMark && (
          <>
            <Box
              data-automation-id="timeline-target-marker"
              data-late={late ? 'true' : 'false'}
              data-offset={String(targetMark.offset)}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${String(targetMark.leftPx)}px`,
                width: 'var(--border-width-thick)',
                bgcolor: targetMark.color,
                pointerEvents: 'none',
              }}
            />
            {/*
              The second hit area, in the strip above the bar. The pad below the
              bar covers the common case — a target at or past the bar's end,
              where there is no bar to cover it — but a target INSIDE the bar is
              hidden behind it and would have no hover at all. This strip stops
              where the bar begins, so it can be painted last without ever
              shadowing a drag.
            */}
            <Box
              data-automation-id="timeline-target-hit-top"
              title={targetMark.title}
              sx={{
                position: 'absolute',
                top: 0,
                height: `${String(BAR_TOP_PX)}px`,
                left: `${String(targetMark.leftPx - TARGET_HIT_WIDTH_PX / 2)}px`,
                width: `${String(TARGET_HIT_WIDTH_PX)}px`,
              }}
            />
          </>
        )}

        {stack && <AllocationStack node={row.node} leftPx={stack.leftPx} />}

        {unscheduled && (
          <Box
            data-automation-id="timeline-unscheduled-pill"
            sx={{
              position: 'absolute',
              top: '7px',
              left: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: 0.625,
              pl: 0.75,
              pr: 1.125,
              py: 0.375,
              border: '1px dashed var(--color-pink-80)',
              borderRadius: 'var(--border-radius-pill)',
              bgcolor: 'var(--bg-panel)',
              pointerEvents: 'none',
            }}
          >
            <MaterialSymbol name="calendar_add_on" size={14} color="var(--color-pink-100)" />
            <Typography variant="label" sx={{ color: 'var(--color-pink-100)' }}>
              {t('timeline.ScheduleRow')}
            </Typography>
          </Box>
        )}

        {emptyParent && (
          <Typography
            variant="label"
            data-automation-id="timeline-empty-parent"
            sx={{
              position: 'absolute',
              top: '9px',
              left: '8px',
              color: 'var(--fg-3)',
              pointerEvents: 'none',
            }}
          >
            {t('timeline.ScheduleBelow')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
